import { ReactElement, ReactNode, useRef, useState } from 'react';
import {
  NativeSyntheticEvent,
  Pressable,
  TextInputFocusEventData,
  View,
} from 'react-native';
import {
  FloatingField,
  IFloatingFieldProps,
  LabeledInput,
} from '@/common/components/Fields/FloatingField/FloatingField';
import { SelectMenuField } from '@/common/components/Fields/SelectField/SelectFieldMenu/SelectMenuField';
import { Heading } from '@/common/components/Heading/Heading';
import { Icon } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { TOverlayRef } from '@/common/components/Overlay/Overlay.types';
import { OverlayBottom } from '@/common/components/Overlay/OverlayBottom/OverlayBottom';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import * as SelectPrimitive from '@rn-primitives/select';
import { styles } from './SelectField.styles';

export function SelectField<T extends SelectPrimitive.Option>(
  props: ISelectFieldProps<T>
) {
  const { sm } = useBreakpoints(['sm']);

  if (sm) {
    return <SelectFieldCenter {...props} />;
  }

  return <SelectFieldBottom {...props} />;
}

// Radix UI doesn't have support for clearing values, so we need to use an empty option as placeholder.
const emptyOption: SelectPrimitive.Option = { value: '', label: '' };

export const SelectFieldCenter = <T extends SelectPrimitive.Option>({
  options,
  value,
  onChange,
  ...props
}: ISelectFloatingFieldProps<T>) => {
  const inputRef = useRef<SelectPrimitive.TriggerRef | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <SelectPrimitive.Root
      value={value ?? emptyOption}
      onValueChange={(val) => {
        const option = options.find((option) => option.value === val?.value);
        onChange(option);
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onOpenChange={setIsOpen}
      className={props.className}
      disabled={props.disabled}
    >
      <SelectFloatingField
        value={value}
        onChange={onChange}
        options={options}
        isOpen={isOpen}
        onPress={() => {
          inputRef.current?.open();
        }}
        focused={isFocused}
        trigger={
          <SelectPrimitive.Trigger
            ref={inputRef}
            disabled={props.disabled}
            className={styles.trigger}
          />
        }
        {...props}
      />
      {isOpen && (
        <SelectMenuField
          options={options}
          className={props.menuClassName}
          renderItem={props.renderItem}
          footer={props.footer}
        />
      )}
    </SelectPrimitive.Root>
  );
};

export const SelectFieldBottom = <T extends SelectPrimitive.Option>({
  id,
  label,
  options,
  value,
  onChange,
  renderItem,
  ...props
}: ISelectFloatingFieldProps<T>) => {
  const bottomOverlayRef = useRef<TOverlayRef>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleSelect = (option: T) => {
    onChange(option);
    setTimeout(() => {
      bottomOverlayRef.current?.close();
    }, 300);
  };

  const renderOptions = () =>
    options.map((option, index) => (
      <Pressable
        key={index}
        className={styles.option}
        onPress={() => handleSelect(option)}
      >
        {renderItem ? (
          renderItem(option)
        ) : (
          <Label bold color="neutral30">
            {option.label}
          </Label>
        )}
        <View className={styles.optionIndicator}>
          {value?.value === option.value && (
            <Icon icon={'check'} color="systemSuccess" size={16} />
          )}
        </View>
      </Pressable>
    ));

  return (
    <>
      <SelectFloatingField
        id={id}
        label={label}
        options={options}
        isOpen={isOpen}
        onPress={handleOpenModal}
        value={value}
        onChange={onChange}
        {...props}
      />
      {isOpen && (
        <OverlayBottom
          header={({ close }) => (
            <>
              <View className={styles.handle} />
              <Stack
                horizontal
                align="center"
                justify="between"
                gap="md"
                className={styles.header}
              >
                <Heading level={3}>{label}</Heading>
                <Pressable onPress={close}>
                  <Icon icon={['far', 'xmark']} size={24} color="neutral20" />
                </Pressable>
              </Stack>
            </>
          )}
          onClose={handleCloseModal}
          ref={bottomOverlayRef}
          hostName="root"
          footer={
            props.footer ? (
              <View className={styles.footer}>{props.footer}</View>
            ) : undefined
          }
        >
          <View className={styles.optionsContainer}>{renderOptions()}</View>
        </OverlayBottom>
      )}
    </>
  );
};

const isDirty = <T,>(value: T | null) => value !== undefined && value !== null;

const SelectFloatingField = <T extends SelectPrimitive.Option>({
  id,
  label,
  focused,
  isOpen,
  onPress,
  value,
  error,
  disabled,
  rightContent,
  hint,
  hintHidden,
  className,
  trigger,
  allowClear,
  onChange,
}: ISelectFloatingFieldProps<T>) => {
  return (
    <FloatingField
      error={error}
      focused={isOpen || focused}
      disabled={disabled}
      hint={hint}
      hintHidden={hintHidden}
      onPress={!disabled ? onPress : undefined}
      className={className}
    >
      {trigger}
      <LabeledInput
        label={label}
        active={!!value}
        inputProps={{
          value: value?.label ?? '',
          id,
          readOnly: true,
          editable: false,
          tabIndex: -1,
          className: !disabled ? 'cursor-pointer' : undefined,
          onPress: !disabled ? onPress : undefined,
          disabled,
        }}
      />

      <Stack horizontal align="center" className="pl-sm">
        {disabled ? <Icon icon="lock" color="neutral20" /> : rightContent}
      </Stack>

      {!disabled && (
        <Stack horizontal align="center" className="pl-sm">
          {allowClear && isDirty(value) ? (
            <Pressable onPress={() => onChange(undefined)}>
              <Icon icon="circle-xmark" color="neutral20" />
            </Pressable>
          ) : (
            <Icon icon={isOpen ? 'chevron-up' : 'chevron-down'} />
          )}
        </Stack>
      )}
    </FloatingField>
  );
};

type FloatingFieldExtendable = Pick<
  IFloatingFieldProps,
  'hint' | 'hintHidden' | 'error' | 'disabled'
>;

export interface ISelectFieldProps<T extends SelectPrimitive.Option>
  extends FloatingFieldExtendable {
  id: string;
  label: string;
  value?: T;
  onChange: (value?: T) => void;
  onBlur?: (evt: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  className?: string;
  menuClassName?: string;
  error?: string;
  rightContent?: ReactNode;
  options: NonNullable<T>[];
  renderItem?: (item: NonNullable<T>) => ReactNode;
  footer?: ReactNode;
  allowClear?: boolean;
}

export interface ISelectFloatingFieldProps<T extends SelectPrimitive.Option>
  extends ISelectFieldProps<T> {
  isOpen?: boolean;
  onPress?: () => void;
  focused?: boolean;
  trigger?: ReactElement;
}
